import axios from 'axios';
import { getToken, storeToken, cleanAuth, getEmail } from '../storage.js';
import { unauthorized, slowNetwork, unhandledNetworkIssue, unpaid } from '../vars';
import { myDebugPrint } from '../debug.js';

// Create an Axios instance
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 90000, // 90 seconds timeout
});

const isPublicAuthRequest = (url = '') => String(url || '').includes('/auth/');

const getResponseDetail = (data) => {
    if (!data) return null;
    if (typeof data === 'string') return data;
    return data.detail ?? data.error ?? data.message ?? null;
};

const normalizeApiErrorMessage = (data, fallback = unhandledNetworkIssue) => {
    const detail = getResponseDetail(data);

    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;

    if (Array.isArray(detail)) {
        const messages = detail
            .map((item) => {
                if (typeof item === 'string') return item;
                if (!item || typeof item !== 'object') return '';

                const field = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : item.field;
                const message = item.msg || item.message || item.detail;

                return [field, message].filter(Boolean).join(': ');
            })
            .filter(Boolean);

        return messages.length > 0 ? messages.join(' ') : fallback;
    }

    if (typeof detail === 'object') {
        return detail.msg || detail.message || detail.detail || fallback;
    }

    return fallback;
};

const buildApiError = (status, data, fallbackMessage = unhandledNetworkIssue) => ({
    code: status,
    status,
    message: normalizeApiErrorMessage(data, fallbackMessage),
    detail: getResponseDetail(data),
    data,
});

// **Request Interceptor** - Automatically adds Authorization token if available
api.interceptors.request.use(
    async (config) => {
        const token = getToken();
        const x_user_email = getEmail() ?? '';
        const authHeaders = [];
        authHeaders.push(`Basic ${process.env.REACT_APP_API_KEY}`)
        if (token) {
            authHeaders.push(`Bearer ${token}`);
        }
        if (x_user_email) {
            config.headers['X-User-Email'] = x_user_email;
        }
        config.headers.Authorization = authHeaders.join(', ');

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// **Response Interceptor** - Handles HTTP errors globally
api.interceptors.response.use(
    async (response) => {
        let newToken = null
        if (response.headers?.jref) {
            newToken = response.headers?.jref;
        } else if (response.headers?.nt) {
            newToken = response.headers?.nt;
        }
        if (newToken) {
            storeToken(newToken);
        }

        if (response.data?.token) {
            storeToken(response.data.token);
        }

        return response;
    },


    async (error) => {
        const response = error.response;
        const normalizedMessage = String(error?.message ?? '').toLowerCase();
        const isTimeoutError = error?.code === 'ECONNABORTED' || normalizedMessage.includes('timeout');
        if (response) {
            // check if the new jwt was received
            const newToken = response.headers?.nt;
            if (newToken) {
                storeToken(newToken);
            } else if (response.data?.token) {
                storeToken(response.data.token);
            }

            // check if the status code was received
            const status = response.status;
            if (status === 401) {
                if (!isPublicAuthRequest(error.config?.url)) {
                    cleanAuth();
                    window.location.href = '/vhod/';
                }
                return Promise.reject({ code: 401, message: unauthorized });
            } else if (status === 402) {
                window.location.href = '/app/myplan';
                return Promise.reject({ code: 402, message: unpaid });
            } else if (`${status}`.startsWith('4')) {
                myDebugPrint('axios -> ided error:', [status, response.data?.detail, response])
                return Promise.reject(buildApiError(status, response.data));
            }

            if (response.data instanceof Blob && response.data.type === 'application/json') {
                try {
                    const text = await response.data.text();
                    const json = JSON.parse(text);

                    if (status === 401) {
                        if (!isPublicAuthRequest(error.config?.url)) {
                            cleanAuth();
                            window.location.href = '/vhod/';
                        }
                        return Promise.reject(buildApiError(401, json, unauthorized));
                    } else if (status === 402) {
                        window.location.href = '/app/myplan';
                        return Promise.reject(buildApiError(402, json, unpaid));
                    }

                    return Promise.reject(buildApiError(status, json));
                } catch (e) {
                    return Promise.reject({ code: status, message: unhandledNetworkIssue });
                }
            }

            return Promise.reject(buildApiError(status, error.response.data));
        }

        if (isTimeoutError) {
            return Promise.reject({
                code: 'ECONNABORTED',
                status: 408,
                message: 'Файл не успел отправиться на сервер. Похоже, соединение слишком медленное или нестабильное. Проверьте интернет и попробуйте снова.',
            });
        }

        // Fallback for no response (network issue, etc.)
        return Promise.reject({ code: 510, message: slowNetwork });
    }
);

export default api;
